import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin } from "lucide-react";

interface LocationData {
  location: string;
  count: number;
}

interface LocationsBySource {
  [source: string]: Record<string, number>;
}

interface LocationChartProps {
  locationStats: LocationData[];
  locationsBySource?: LocationsBySource;
  selectedSource?: string;
}

// Color palette for charts
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
  "#FFC658",
  "#FF7C7C",
  "#8DD1E1",
  "#D084D0",
  "#FFAB0F",
  "#67B7DC",
  "#A4DE6C",
  "#FFC0CB",
  "#FFE4B5",
];

export const LocationChart: React.FC<LocationChartProps> = ({
  locationStats,
  locationsBySource,
  selectedSource,
}) => {
  // Prepare data for charts
  const chartData =
    selectedSource && locationsBySource?.[selectedSource]
      ? Object.entries(locationsBySource[selectedSource])
          .map(([location, count]) => ({ location, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 15) // Top 15 locations
      : locationStats.slice(0, 15);

  const pieData = chartData.slice(0, 8); // Top 8 for pie chart
  const totalVisitors = chartData.reduce((sum, item) => sum + item.count, 0);

  const formatTooltip = (value: any, name: string) => {
    if (name === "count") {
      const percentage = ((value / totalVisitors) * 100).toFixed(1);
      return [`${value} visitors (${percentage}%)`, "Visitors"];
    }
    return [value, name];
  };

  const formatLabel = (location: string) => {
    // Truncate long location names for better display
    return location.length > 20 ? location.substring(0, 17) + "..." : location;
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location Summary
            {selectedSource && (
              <span className="text-sm font-normal text-gray-600">
                - {selectedSource.replace("_", " ")}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {totalVisitors}
              </div>
              <div className="text-xs text-gray-600">Total Visitors</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {chartData.length}
              </div>
              <div className="text-xs text-gray-600">Unique Locations</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {chartData[0]?.count || 0}
              </div>
              <div className="text-xs text-gray-600">Top Location</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {chartData[0]
                  ? ((chartData[0].count / totalVisitors) * 100).toFixed(1)
                  : 0}
                %
              </div>
              <div className="text-xs text-gray-600">Top Location %</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bar Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Visitor Distribution by Location</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="location"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatLabel}
                />
                <YAxis />
                <Tooltip formatter={formatTooltip} />
                <Bar dataKey="count" fill="#0088FE" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Pie Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Top Locations Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="h-80 w-full md:w-1/2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ location, percent }) =>
                      `${formatLabel(location)} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={formatTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="w-full md:w-1/2">
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900">Location Legend</h4>
                {pieData.map((item, index) => (
                  <div key={item.location} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm text-gray-700 flex-1">
                      {item.location}
                    </span>
                    <span className="text-sm font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Location Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-semibold">Rank</th>
                  <th className="text-left p-2 font-semibold">Location</th>
                  <th className="text-right p-2 font-semibold">Visitors</th>
                  <th className="text-right p-2 font-semibold">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item, index) => (
                  <tr key={item.location} className="border-b hover:bg-gray-50">
                    <td className="p-2 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-primary text-primary-foreground rounded-full text-sm font-bold">
                        {index + 1}
                      </span>
                    </td>
                    <td className="p-2 font-medium">{item.location}</td>
                    <td className="p-2 text-right font-semibold">
                      {item.count}
                    </td>
                    <td className="p-2 text-right text-gray-600">
                      {((item.count / totalVisitors) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
